/**
 * Fuel governor. Advisory in V1: it explains, it never acts.
 *
 * The simulator compares what each worker count buys in time against what it
 * costs in capacity, so "more agents" is visibly not the same as "faster".
 */
import type { GovernorRecommendation } from '@dcc/shared';
import { Panel, Unknown } from './Primitives';
import { duration, relativeTime } from '../lib/format';

export function GovernorPanel({ governor }: { governor: GovernorRecommendation }) {
  const delta = governor.recommendedWorkers - governor.currentWorkers;
  const tone = delta < 0 ? 'risk' : delta > 0 ? 'active' : 'success';

  return (
    <Panel
      title="Fuel governor"
      subtitle="Advisory. It optimises completed journeys per unit of capacity, not the number of running agents."
      tone={tone}
      actions={
        <span className="chip" data-tone={governor.dispatchAllowed ? 'success' : 'risk'}>
          {governor.dispatchAllowed ? 'dispatch allowed' : 'dispatch frozen'}
        </span>
      }
    >
      <div className="stack">
        <div className="spread">
          <div>
            <div className="label">Recommendation</div>
            <strong style={{ fontSize: 17 }}>{governor.headline}</strong>
            <p className="small dim">
              {governor.currentWorkers} running now · policy ceiling {governor.ceiling}
            </p>
          </div>
          <span
            className="stat__value stat__value--lg"
            data-tone={tone}
            style={{ color: 'var(--tone)' }}
          >
            {governor.recommendedWorkers}
          </span>
        </div>

        {!governor.dispatchAllowed && (
          <div className="banner" data-tone="risk" role="status">
            <span className="chip__glyph" aria-hidden="true">
              ▲
            </span>
            <span className="banner__text">{governor.dispatchBlockReasons.join(' ')}</span>
          </div>
        )}

        <div>
          <div className="label">Why</div>
          <ul className="small stack stack--tight" style={{ margin: '6px 0 0', paddingLeft: 16 }}>
            {governor.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>

        <div className="scroll-x">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${governor.options.length}, minmax(160px, 1fr))`,
            }}
          >
            {governor.options.map((option) => {
              const recommended = option.workers === governor.recommendedWorkers;
              return (
                <div
                  key={option.workers}
                  className="card card--toned"
                  data-tone={recommended ? 'success' : option.allowed ? 'neutral' : 'risk'}
                >
                  <div className="spread">
                    <strong>
                      {option.workers} worker{option.workers === 1 ? '' : 's'}
                    </strong>
                    {recommended && (
                      <span className="chip" data-tone="success">
                        recommended
                      </span>
                    )}
                  </div>
                  <div className="stat__value" style={{ fontSize: 20, marginTop: 8 }}>
                    {option.finishHours === null ? <Unknown /> : duration(option.finishHours)}
                  </div>
                  <p className="tiny dim">
                    {option.etaAt ? `finishes ${relativeTime(option.etaAt)}` : 'no finish derived'}
                  </p>
                  <dl
                    className="tiny"
                    style={{
                      margin: '8px 0 0',
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 3,
                    }}
                  >
                    <dt className="dim">Capacity burned</dt>
                    <dd className="mono" style={{ margin: 0 }}>
                      {option.burnPercent === null ? '—' : `${option.burnPercent}%`}
                    </dd>
                    <dt className="dim">Time saved</dt>
                    <dd className="mono" style={{ margin: 0 }}>
                      {option.marginalThroughputGain === null
                        ? '—'
                        : `${(option.marginalThroughputGain * 100).toFixed(0)}%`}
                    </dd>
                    <dt className="dim">Extra burn</dt>
                    <dd className="mono" style={{ margin: 0 }}>
                      {option.marginalBurnIncrease === null
                        ? '—'
                        : `${(option.marginalBurnIncrease * 100).toFixed(0)}%`}
                    </dd>
                    <dt className="dim">Journeys / capacity</dt>
                    <dd className="mono" style={{ margin: 0 }}>
                      {option.journeysPerCapacityUnit === null
                        ? '—'
                        : option.journeysPerCapacityUnit.toFixed(4)}
                    </dd>
                  </dl>
                  {!option.allowed && option.rejectionReason && (
                    <p
                      className="tiny"
                      data-tone="risk"
                      style={{ color: 'var(--risk)', marginTop: 8 }}
                    >
                      {option.rejectionReason}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <div className="label">Constraints considered</div>
          <div className="rows" style={{ marginTop: 6 }}>
            {governor.constraints.map((constraint) => (
              <div
                key={constraint.name}
                className="row"
                style={{ gridTemplateColumns: '1fr auto', padding: '8px 0' }}
              >
                <div className="row__stack row__cell" data-label="Constraint">
                  <span>{constraint.name}</span>
                  <span className="row__sub">{constraint.detail}</span>
                </div>
                <span className="mono row__cell" data-label="Limit">
                  {constraint.limit}
                </span>
              </div>
            ))}
          </div>
        </div>

        <p className="tiny dim mono">
          policy {governor.policyVersion} · evaluated {relativeTime(governor.evaluatedAt)}
        </p>
      </div>
    </Panel>
  );
}
